import { type Ref, useEffect, useRef } from "react";

import { Column, EmptyState } from "@hightouch/htui";
import { matchSorter } from "match-sorter";

import { columnReferenceKey } from "src/components/explore/filter-popover/utils";
import { useDestinationForm } from "src/contexts/destination-form-context";
import type { ColumnCategory } from "src/formkit/components/mapper-v2/column-categories";
import { useMapperListbox } from "src/formkit/components/mapper-v2/hooks/use-mapper-listbox";
import {
  MapperList,
  MapperListRow,
  MapperListSkeleton,
  MapperSearch,
} from "src/formkit/components/mapper-v2/mapper-list";
import {
  getValueType,
  type MapperValue,
} from "src/formkit/components/mapper-v2/mapper-selection";
import { MappingType } from "src/formkit/components/types";

type ArrayPropertiesPaneProps = {
  /** The array's element properties. */
  category: ColumnCategory | undefined;
  /** The committed mapping value, whose property is shown as chosen. */
  value: MapperValue;
  search: string;
  onSearchChange: (search: string) => void;
  searchRef: Ref<HTMLInputElement>;
  onChoose: (value: MapperValue) => void;
  onClose: () => void;
};

type PropertyRow = {
  key: string;
  label: string;
  /** Undefined for the committed property, which closes the popover instead. */
  from: string | undefined;
  isSelected: boolean;
};

/**
 * The element properties of the array a nested row maps from. A property that
 * isn't listed can be typed in as an expected value.
 */
export function ArrayPropertiesPane({
  category,
  value,
  search,
  onSearchChange,
  searchRef,
  onChoose,
  onClose,
}: ArrayPropertiesPaneProps) {
  const hasCheckedRowsRef = useRef(false);
  const { loadingRows, rows, reloadRows } = useDestinationForm();
  const committedFrom =
    getValueType(value) === MappingType.STANDARD ? value.from : undefined;
  const committedColumn =
    typeof committedFrom === "string" ? committedFrom : undefined;
  const committedKey =
    committedFrom === undefined ? undefined : columnReferenceKey(committedFrom);

  // The element properties come from preview rows, so the pane runs a preview when there are none.
  // Only the rows at mount decide, so a preview that returns no rows isn't requested again.
  useEffect(() => {
    if (hasCheckedRowsRef.current) return;
    hasCheckedRowsRef.current = true;
    if (!rows?.length) reloadRows();
  }, [rows, reloadRows]);

  const properties = (category?.options ?? []).flatMap((option) =>
    typeof option.value === "string"
      ? [{ label: option.label, value: option.value }]
      : [],
  );
  const trimmedSearch = search.trim();
  const filteredProperties = trimmedSearch
    ? matchSorter(properties, trimmedSearch, { keys: ["label"] })
    : properties;
  const searchMatchesExisting =
    trimmedSearch === committedFrom ||
    filteredProperties.some((property) => property.label === trimmedSearch);
  const isCommittedListed = properties.some(
    (property) => property.value === committedFrom,
  );

  const propertyRows: PropertyRow[] = [
    ...(trimmedSearch && !searchMatchesExisting
      ? [
          {
            key: `create:${trimmedSearch}`,
            label: `Use expected column name "${trimmedSearch}"…`,
            from: trimmedSearch,
            isSelected: false,
          },
        ]
      : []),
    ...filteredProperties.map((property) => {
      const isSelected = columnReferenceKey(property.value) === committedKey;
      return {
        key: property.value,
        label: property.label,
        from: isSelected ? undefined : property.value,
        isSelected,
      };
    }),
    ...(committedColumn && !isCommittedListed
      ? [
          {
            key: `committed:${committedColumn}`,
            label: `Use expected column name "${committedColumn}"…`,
            from: undefined,
            isSelected: true,
          },
        ]
      : []),
  ];

  const chooseRow = (row: PropertyRow) => {
    if (row.from === undefined) {
      onClose();
    } else {
      onChoose({ from: row.from });
    }
  };

  const listbox = useMapperListbox({
    optionCount: loadingRows ? 0 : propertyRows.length,
    hasSearch: Boolean(trimmedSearch),
    onChoose: (index) => {
      const row = propertyRows[index];
      if (row) chooseRow(row);
    },
  });

  return (
    <>
      <MapperSearch
        inputRef={searchRef}
        label="Search columns"
        listbox={listbox}
        placeholder="Search columns…"
        value={search}
        onChange={(nextSearch) => {
          onSearchChange(nextSearch);
          listbox.resetActiveIndex();
        }}
      />
      {loadingRows ? (
        <MapperListSkeleton />
      ) : propertyRows.length > 0 ? (
        <MapperList
          activeOptionId={listbox.activeOptionId}
          label="Array properties"
          listId={listbox.listId}
          scrollRef={listbox.scrollRef}
        >
          {propertyRows.map((row, index) => (
            <MapperListRow
              key={row.key}
              id={listbox.getOptionId(index)}
              isActive={index === listbox.activeIndex}
              isSelected={row.isSelected}
              label={row.label}
              onChoose={() => chooseRow(row)}
            />
          ))}
        </MapperList>
      ) : (
        <Column flex={1} justifyContent="center" p={6}>
          <EmptyState
            message="Enter the name of an expected property to map it."
            title="No array properties found"
            variant="minimal"
          />
        </Column>
      )}
    </>
  );
}
